import type { CSSProperties } from 'react';

/**
 * Skeletons: the shape of what is coming, drawn before it arrives.
 *
 * The loading system's rule for the content area is "skeletons only, in the
 * exact geometry of what's coming" - a list waits as 48px rows, a record as
 * label/value pairs, a chart as bars at plausible heights. A bare spinner in
 * the middle of an empty pane says nothing about what the screen will look
 * like and makes the layout jump when it lands.
 *
 *   <SkeletonRows rows={5} />   Table, Today, CRM, History
 *   <SkeletonCards cards={4} /> Tiles, Kanban, Pipeline, Home widgets
 *   <SkeletonRecord />          Properties rail, Request tab, preview panel
 *   <SkeletonChart />           Market pulse, Gantt, Map canvas
 *   <SkeletonText />            Report body, briefing, document preview
 *   <SkeletonPage />            A whole routed screen: heading, briefing, rows
 *
 * All of them are decorative (`aria-hidden`): the surrounding `<Loading>`
 * carries the `role="status"` and the words a screen reader announces.
 */

export interface SkeletonProps {
  /** CSS width - `'60%'`, `120`, `'12ch'`. */
  width?: number | string;
  height?: number | string;
  /** `pill` for chips, `circle` for avatars and status dots. */
  shape?: 'bar' | 'pill' | 'circle' | 'block';
  className?: string;
  style?: CSSProperties;
}

const px = (value: number | string | undefined) => (typeof value === 'number' ? `${value}px` : value);

/** One shimmering bar. Everything else here is built from it. */
export function Skeleton({ width, height, shape = 'bar', className, style }: SkeletonProps) {
  return (
    <span
      className={['cw-skel', `cw-skel--${shape}`, className].filter(Boolean).join(' ')}
      style={{ width: px(width), height: px(height), ...style }}
      aria-hidden="true"
    />
  );
}

/*
 * Widths vary row to row so a list reads as a list of different things rather
 * than a barcode. Deterministic, so a re-render does not make the bars dance.
 */
const TITLE_WIDTHS = ['46%', '38%', '52%', '42%', '34%', '48%'];
const SUB_WIDTHS = ['64%', '58%', '70%', '54%', '62%', '66%'];
const CARD_TITLE_WIDTHS = ['72%', '64%', '80%', '68%'];

export interface SkeletonRowsProps {
  /** 3-6 reads as "a list"; more is noise. Clamped to that range. */
  rows?: number;
  /** Rows still to come past the ones drawn, for "Loading n more". */
  more?: number;
  /** Leading avatar/status column, as on Today and the CRM lists. */
  leading?: boolean;
  className?: string;
}

/** List rows at the portal's 48px row height: status, two-line title, trailing cells. */
export function SkeletonRows({ rows = 4, more, leading = true, className }: SkeletonRowsProps) {
  const count = Math.min(6, Math.max(3, rows));

  return (
    <div className={['cw-skel-rows', className].filter(Boolean).join(' ')} aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="cw-skel-row">
          {leading && (
            <>
              <Skeleton shape="circle" width={10} height={10} />
              <Skeleton width={44} height={8} />
            </>
          )}
          <span className="cw-skel-row__main">
            <Skeleton width={TITLE_WIDTHS[index % TITLE_WIDTHS.length]} height={9} />
            <Skeleton width={SUB_WIDTHS[index % SUB_WIDTHS.length]} height={7} />
          </span>
          <Skeleton className="cw-skel-row__cell" width={76} height={14} shape="block" />
          <Skeleton className="cw-skel-row__cell" width={92} height={8} />
          <Skeleton className="cw-skel-row__cell" width={44} height={8} />
        </div>
      ))}
      {more !== undefined && more > 0 && <p className="cw-skel-rows__more">Loading {more} more…</p>}
    </div>
  );
}

export interface SkeletonCardsProps {
  cards?: number;
  className?: string;
}

/** Cards with the real card's padding: kicker, title, meta line, progress rule, footer. */
export function SkeletonCards({ cards = 4, className }: SkeletonCardsProps) {
  return (
    <div className={['cw-skel-cards', className].filter(Boolean).join(' ')} aria-hidden="true">
      {Array.from({ length: cards }, (_, index) => (
        <div key={index} className="cw-skel-card">
          <Skeleton width="34%" height={7} />
          <Skeleton width={CARD_TITLE_WIDTHS[index % CARD_TITLE_WIDTHS.length]} height={10} />
          <Skeleton width="58%" height={8} />
          <Skeleton className="cw-skel-card__rule" width="100%" height={3} />
          <span className="cw-skel-card__foot">
            <Skeleton width="38%" height={7} />
            <Skeleton width={56} height={16} shape="block" />
          </span>
        </div>
      ))}
    </div>
  );
}

export interface SkeletonRecordProps {
  fields?: number;
  className?: string;
}

/** Label/value pairs. The labels shimmer too - they arrive with the record. */
export function SkeletonRecord({ fields = 6, className }: SkeletonRecordProps) {
  return (
    <div className={['cw-skel-record', className].filter(Boolean).join(' ')} aria-hidden="true">
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} className="cw-skel-record__pair">
          <Skeleton width={[64, 52, 72, 48, 60, 56][index % 6]} height={8} />
          <Skeleton width={SUB_WIDTHS[index % SUB_WIDTHS.length]} height={9} />
        </div>
      ))}
    </div>
  );
}

export interface SkeletonChartProps {
  bars?: number;
  className?: string;
}

const BAR_HEIGHTS = [38, 52, 44, 66, 80, 94, 72, 58];

/** Bars at plausible heights on a baseline. No axis text: the numbers are what is loading. */
export function SkeletonChart({ bars = 6, className }: SkeletonChartProps) {
  return (
    <div className={['cw-skel-chart', className].filter(Boolean).join(' ')} aria-hidden="true">
      <div className="cw-skel-chart__bars">
        {Array.from({ length: bars }, (_, index) => (
          <Skeleton key={index} shape="block" height={`${BAR_HEIGHTS[index % BAR_HEIGHTS.length]}%`} />
        ))}
      </div>
      <Skeleton width="62%" height={7} />
    </div>
  );
}

export interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

const LINE_WIDTHS = ['96%', '91%', '94%', '72%', '88%', '93%', '60%'];

/** Paragraph lines, the last one short - a report body or a briefing. */
export function SkeletonText({ lines = 4, className }: SkeletonTextProps) {
  return (
    <div className={['cw-skel-text', className].filter(Boolean).join(' ')} aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} width={index === lines - 1 ? '42%' : LINE_WIDTHS[index % LINE_WIDTHS.length]} height={9} />
      ))}
    </div>
  );
}

/**
 * A whole routed screen: the page heading, a briefing block and a grouped list.
 * The geometry every list-led screen (Today, Assignments, CRM, Opportunities)
 * shares, so a route chunk in flight already looks like the screen it becomes.
 */
export function SkeletonPage({ className }: { className?: string }) {
  return (
    <div className={['cw-skel-page', className].filter(Boolean).join(' ')} aria-hidden="true">
      <div className="cw-skel-page__head">
        <Skeleton width={180} height={12} />
        <Skeleton width={260} height={8} />
      </div>
      <div className="cw-skel-page__brief">
        <SkeletonText lines={3} />
        <span className="cw-skel-page__chips">
          <Skeleton width={110} height={18} shape="block" />
          <Skeleton width={92} height={18} shape="block" />
          <Skeleton width={130} height={18} shape="block" />
        </span>
      </div>
      <div className="cw-skel-page__group">
        <Skeleton width={120} height={9} />
      </div>
      <SkeletonRows rows={4} />
    </div>
  );
}
